import { queryClient } from "@istari-frontend/api-client";
import { parseIdentityClaims } from "@istari-frontend/auth";
import { type ReactNode, useEffect, useMemo, useRef } from "react";
import { useAuth } from "react-oidc-context";

import { currentUserIdentityFromProfile } from "~/user/model/current-user-identity";

import { identityMode } from "../model/identity-mode";
import { sessionLacksAccess } from "../model/no-access";
import type { Session } from "../model/session";
import { NoAccessScreen } from "../ui/no-access-screen";
import { deriveSessionFromClaims } from "./derive-session-from-claims";
import { useLegacySession } from "./legacy-session";
import { SessionContext } from "./session-context";

type Profile = Record<string, unknown> | undefined;

function IdentityV2Session({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const profile = auth.user?.profile as Profile;
  // Recomputed whenever the OIDC library hands over a new user, so a renewed token's roles apply at once.
  const session = useMemo<Session>(
    () => ({
      mode: "identity-v2",
      // Loading until the OIDC library settles with a signed-in user whose profile is readable, so a
      // guard never treats the brief authenticated-but-no-profile window as a member and redirects.
      isLoading: (auth.isLoading && !auth.isAuthenticated) || (auth.isAuthenticated && profile === undefined),
      ...deriveSessionFromClaims(parseIdentityClaims(profile)),
    }),
    [profile, auth.isLoading, auth.isAuthenticated],
  );
  useResetCacheOnViewerChange(session.principalId, session.tenantId);
  return (
    <SessionContext.Provider value={session}>
      {sessionLacksAccess(session) ? (
        <NoAccessScreen reason="no_membership" onSignOut={() => void auth.signoutRedirect()} />
      ) : (
        children
      )}
    </SessionContext.Provider>
  );
}

function LegacySession({ mode, children }: { mode: "zitadel-direct" | "identity-v1"; children: ReactNode }) {
  const auth = useAuth();
  const profile = auth.user?.profile as Profile;
  const session = useLegacySession(
    mode,
    parseIdentityClaims(profile),
    currentUserIdentityFromProfile(profile).upstreamUserId,
  );
  // Legacy tokens name no principal; the provider's subject is the account identity there.
  useResetCacheOnViewerChange(auth.user?.profile.sub, session.tenantId);
  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
}

// Query keys carry no viewer, so another account, or the same one in another tenant, must not see the old cache.
function useResetCacheOnViewerChange(accountId: string | undefined, tenantId: string | undefined) {
  const viewer = accountId === undefined ? undefined : `${accountId}:${tenantId ?? ""}`;
  const lastViewer = useRef(viewer);
  useEffect(() => {
    if (viewer === undefined) return;
    if (lastViewer.current !== undefined && lastViewer.current !== viewer) queryClient.clear();
    lastViewer.current = viewer;
  }, [viewer]);
}

/** Provides one session model whatever the identity mode; consumers read it with useSession(). */
export function SessionProvider({ children }: { children: ReactNode }) {
  const mode = identityMode();
  return mode === "identity-v2" ? (
    <IdentityV2Session>{children}</IdentityV2Session>
  ) : (
    <LegacySession mode={mode}>{children}</LegacySession>
  );
}
